import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// DeLorean DMC-12 (1981) — the folded-paper Giugiaro wedge at true scale
// (1 unit = 1 metre): length ~4.27, an emphatically WIDE ~1.96, a very LOW ~0.96
// tall, wheelbase 2.41, ~0.32 m wheels. Nose is +Z. Authored for the `cars`
// showcase batch.
//
// The silhouette is a low, wide, angular wedge of flat faceted planes and sharp
// straight edges — an unmistakable LOW WIDE LETTERBOX from the front and back
// (about 2 : 1), never a tall cab. A low blunt nose rises through a near-flat
// hood and a hard-raked flat windscreen to a low flat roof; the greenhouse is a
// thin low glass slot, not a stacked box. Behind it a raked rear window buried
// under horizontal BLACK LOUVRES falls to a short engine deck and a flat Kamm
// tail — ONE continuous descending face (no flat open deck / no pickup bed).
// The defining cues: BARE BRUSHED STAINLESS STEEL bodywork (flat medium grey,
// on the "paint" slot so it can still be recoloured), a prominent BLACK rubber
// LOWER CLADDING band wrapping the whole car (nose valance, sills, wheel arches,
// rear valance), GULLWING door cut-lines arcing up onto the roof edge, two wide
// flat rectangular headlamps spanning most of the nose over a thin black grille
// slot, the black louvred engine cover, a black slatted tail panel with wide
// ribbed tail lamps and twin exhausts, and the DMC-12's bright multi-spoke
// turbine alloys on low-profile tyres. `drive` spins the wheels and `hazards`
// blinks the four indicators.

const PAINT = "#9fa4a8"; // brushed stainless steel grey — the "paint" slot default
const TRIM = "#141518"; // gloss black (pillars, louvres, grille, tail panel)
const CLAD = "#1a1b1d"; // BLACK rubber lower cladding band + bumpers (reads distinctly)
const WELL = "#111214"; // wheel-well discs
const GLASS = "#1b2129"; // smoked side / rear glazing
const SCREEN = "#26333f"; // windscreen (a shade lighter than the tint)
const CABIN = "#0e0f12"; // interior block behind the glass
const SPOKE = "#bcc1c7"; // bright turbine-alloy face
const DISH = "#101114"; // dark turbine grooves + wheel dish
const TIRE = "#1a1b1d";
const BRAKE = "#8c9196";
const LAMP = "#e8f0f4"; // headlamp glass
const AMBER = "#e8a13a";
const TAIL_RED = "#a5182a"; // wide ribbed tail lamps
const REDDARK = "#6f1019"; // tail-lamp ribs / inner
const CHROME = "#ccd1d6";
const EXHAUST = "#2b2e33"; // exhaust tip metal (dark gunmetal)
const PLATE = "#eceff2";

// Spec-sheet skeleton (metres), pushed decisively LOW and WIDE so every square
// view is an unmistakable letterbox: the belt line sits at only ~0.77 and the
// flat roof caps at ~0.96, while the body runs 1.88 wide and the tyres plant out
// to ~1.96, so the FRONT/BACK read about 2 : 1 (clearly wider than tall).
const WHEEL_R = 0.32;
const AXLE_F = 1.15;
const AXLE_R = -1.26; // wheelbase 2.41
const WHEEL_X_F = 0.85; // half track, front — wide planted stance
const WHEEL_X_R = 0.86; // half track, rear
const BODY_X = 0.94; // main steel tub half-width (body 1.88)

// The wedge's greenhouse breakpoints (side profile, z -> top-surface y):
//   cowl (0.80, 0.77) -> roof front (0.32, 0.96) -> roof rear (-0.58, 0.96)
//   -> deck start (-1.18, 0.79) -> lid end (-1.82, 0.74) -> tail top (-2.05, 0.71)
// The fastback falls in three overlapping full-width tilted boxes whose top
// surfaces meet on those breakpoints, so roof -> louvred window -> engine lid
// -> tail deck is ONE continuous descending mass (never a flat open deck).
const HOOD_SLOPE = 0.05; // near-flat hood, front edge just lower than the cowl
const SCREEN_RAKE = 1.126; // hard-raked flat windscreen (laid well back — low wedge)
const SAIL_ANGLE = 0.276; // steep louvred rear window
const LID_ANGLE = 0.078; // engine lid
const DECK_ANGLE = 0.13; // short tail deck onto the Kamm tail

function box(
  w: number, h: number, d: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation, ...extra };
}

function cyl(
  rTop: number, rBot: number, h: number, seg: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.CylinderGeometry(rTop, rBot, h, seg), color, position, rotation, ...extra };
}

/** A painted body panel — carries the "paint" recolour slot. */
function paint(w: number, h: number, d: number, position: Vec3, rotation?: Vec3): Part {
  return box(w, h, d, PAINT, position, rotation, { slot: "paint" });
}

/** One DMC-12 turbine alloy: tyre, brake disc, a bright dished face carrying
 *  twelve dark radial grooves (the turbine vanes), a bright centre boss and a
 *  chrome cap. Every part shares `name` so the `drive` track spins them as one
 *  node about the axle. `side` is +1 for the right (+x) wheel, -1 for the left. */
function wheel(side: 1 | -1, halfTrack: number, tyreW: number, z: number, name: string): Part[] {
  const y = WHEEL_R;
  const outer = halfTrack + tyreW / 2; // tyre outer face — stays proud of the well disc
  const parts: Part[] = [
    cyl(WHEEL_R, WHEEL_R, tyreW, 20, TIRE, [side * halfTrack, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.13, 0.13, 0.03, 14, BRAKE, [side * (halfTrack - 0.05), y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.255, 0.255, 0.05, 24, SPOKE, [side * (outer - 0.03), y, z], [0, 0, Math.PI / 2], { name }), // bright turbine face
  ];
  for (let k = 0; k < 12; k++) {
    parts.push(
      box(0.018, 0.44, 0.02, DISH, [side * (outer - 0.008), y, z], [(k * Math.PI) / 6, 0, 0], { name }), // dark turbine groove
    );
  }
  parts.push(cyl(0.1, 0.1, 0.05, 16, SPOKE, [side * (outer - 0.002), y, z], [0, 0, Math.PI / 2], { name })); // centre boss
  parts.push(cyl(0.045, 0.045, 0.03, 12, CHROME, [side * (outer + 0.008), y, z], [0, 0, Math.PI / 2], { name })); // chrome cap
  return parts;
}

/** The opened wheel well: a dark disc proud of the body side (the arch "cut")
 *  plus a BLACK cladding half-torus brow over the tyre (the arch is part of the
 *  black lower band). The [0, pi/2, 0] yaw puts the 0..pi arc exactly over the
 *  top half. The tyre's outer face stays proud of both. */
function arch(side: 1 | -1, z: number, discR: number): Part[] {
  return [
    cyl(discR, discR, 0.04, 20, WELL, [side * (BODY_X + 0.01), WHEEL_R, z], [0, 0, Math.PI / 2]),
    {
      geometry: new THREE.TorusGeometry(discR - 0.02, 0.055, 6, 20, Math.PI),
      color: CLAD,
      position: [side * (BODY_X + 0.025), WHEEL_R, z] as Vec3,
      rotation: [0, Math.PI / 2, 0] as Vec3,
    },
  ];
}

// The four hazard indicators — parts and blink tracks are generated from one
// table so the pivots (each part's own centre) can never drift from the parts.
// Front pair sits in the black nose valance; rear pair outboard of the tail lamps.
const INDICATORS: ReadonlyArray<{ name: string; pos: Vec3; size: Vec3 }> = [
  { name: "indicator-fl", pos: [-0.63, 0.42, 2.12], size: [0.14, 0.05, 0.04] },
  { name: "indicator-fr", pos: [0.63, 0.42, 2.12], size: [0.14, 0.05, 0.04] },
  { name: "indicator-rl", pos: [-0.82, 0.58, -2.1], size: [0.09, 0.09, 0.04] },
  { name: "indicator-rr", pos: [0.82, 0.58, -2.1], size: [0.09, 0.09, 0.04] },
];

function parts(): Part[] {
  const P: Part[] = [];
  const S = [1, -1] as const;

  // -- Running gear (planted at the wide corners on low-profile turbine alloys)
  P.push(
    ...wheel(-1, WHEEL_X_F, 0.26, AXLE_F, "wheel-fl"), ...wheel(1, WHEEL_X_F, 0.26, AXLE_F, "wheel-fr"),
    ...wheel(-1, WHEEL_X_R, 0.28, AXLE_R, "wheel-rl"), ...wheel(1, WHEEL_X_R, 0.28, AXLE_R, "wheel-rr"),
  );
  for (const z of [AXLE_F, AXLE_R]) {
    P.push(cyl(0.05, 0.05, 1.58, 8, TRIM, [0, WHEEL_R, z], [0, 0, Math.PI / 2])); // axle
    for (const s of S) P.push(...arch(s, z, 0.42));
  }
  P.push(box(1.5, 0.14, 3.4, TRIM, [0, 0.15, -0.05])); // underbody pan

  // -- Body masses: a low WIDE steel tub over a prominent BLACK cladding band --
  P.push(paint(1.88, 0.32, 3.5, [0, 0.61, -0.05])); // steel tub, sill to belt line (y 0.45..0.77)
  P.push(box(1.9, 0.35, 3.5, CLAD, [0, 0.295, -0.05])); // BLACK lower cladding band wrapping the whole lower body (y 0.12..0.47), proud of the tub

  // -- Wedge nose: near-flat hood, flat fender tops, low steel prow ------------
  P.push(paint(1.76, 0.1, 1.24, [0, 0.74, 1.42], [HOOD_SLOPE, 0, 0])); // near-flat hood
  for (const s of S) P.push(paint(0.1, 0.1, 1.24, [s * 0.88, 0.745, 1.42], [HOOD_SLOPE, 0, 0])); // flat fender tops beside the hood
  P.push(paint(1.84, 0.24, 0.14, [0, 0.6, 2.02])); // low steel nose prow face (front z 2.09, top 0.72)
  P.push(box(1.68, 0.24, 0.6, CLAD, [0, 0.35, 1.72])); // black valance filling under the nose to the front arch

  // -- Rear valance fill (black, back to the rear arch) -----------------------
  P.push(box(1.68, 0.24, 0.6, CLAD, [0, 0.35, -1.7]));

  // -- Greenhouse: a NARROW low cockpit slot inset into wide steel shoulders
  //    (the tub top at 0.77 shows either side as a shoulder), so the front reads
  //    as a sleek wide body with a cockpit — never a full-width van cab.
  P.push(box(1.4, 0.03, 0.12, TRIM, [0, 0.77, 0.85])); // cowl vent at the windscreen base
  P.push(box(1.42, 0.44, 0.045, SCREEN, [0, 0.865, 0.6], [-SCREEN_RAKE, 0, 0])); // short flat hard-raked windscreen
  for (const s of S) P.push(paint(0.11, 0.44, 0.08, [s * 0.7, 0.865, 0.6], [-SCREEN_RAKE, 0, 0])); // A-pillars
  P.push(paint(1.48, 0.06, 0.98, [0, 0.93, -0.09])); // low NARROW flat roof (top 0.96), inset in the shoulders
  P.push(box(1.4, 0.2, 1.05, CABIN, [0, 0.8, -0.02])); // dark interior block under the roof
  for (const s of S) {
    P.push(box(0.03, 0.19, 0.86, GLASS, [s * 0.72, 0.865, -0.08], [0, 0, s * 0.03])); // low side glass slot, inboard of the roof
    P.push(box(0.05, 0.19, 0.06, TRIM, [s * 0.725, 0.865, -0.52], [0, 0, s * 0.03])); // B-pillar
    // GULLWING door cut-lines: thin recessed dark grooves arcing along the roof
    // edge (the door skins cut up into the roof), closed at the front by the pillar.
    P.push(box(0.035, 0.02, 0.9, TRIM, [s * 0.7, 0.955, -0.09])); // roof-edge cut groove
    P.push(box(0.03, 0.02, 0.5, TRIM, [s * 0.76, 0.775, 0.05])); // cut line down onto the shoulder / door
  }

  // -- Fastback rear as ONE continuous descending mass (no flat deck / no bed):
  //    a steep louvred rear window, a shallow engine lid, a short tail deck —
  //    full-width steel boxes overlapping edge-to-edge down to the Kamm tail,
  //    so the rear flanks are solid with no ledge behind the cabin.
  P.push(paint(1.86, 0.44, 0.74, [0, 0.66, -0.88], [-SAIL_ANGLE, 0, 0])); // louvred-window sail (steel quarters flank the louvres)
  P.push(paint(1.88, 0.34, 0.72, [0, 0.6, -1.5], [-LID_ANGLE, 0, 0])); // engine lid
  P.push(paint(1.88, 0.26, 0.34, [0, 0.6, -1.94], [-DECK_ANGLE, 0, 0])); // tail deck onto the Kamm tail
  // Black louvre slats lying on the sail, over its centre (steel shows either side).
  for (let i = 0; i < 6; i++) {
    const z = -0.62 - 0.09 * i; // -0.62 .. -1.07
    const y = 0.96 + 0.283 * (z + 0.58) + 0.03; // ride the sail slope, proud of the steel
    P.push(box(1.36, 0.03, 0.05, TRIM, [0, y, z], [-SAIL_ANGLE, 0, 0])); // louvre slat
  }
  for (let i = 0; i < 3; i++) P.push(box(1.2, 0.02, 0.04, "#26282c", [i * 0.34 - 0.34, 0.79, -1.52], [-LID_ANGLE, 0, 0])); // engine-lid vent lines

  // -- Front fascia: two wide flat rectangular headlamps + thin black grille ---
  P.push(box(1.32, 0.05, 0.06, TRIM, [0, 0.66, 2.1])); // thin wide black grille slot below the hood lip
  for (const s of S) {
    P.push(box(0.66, 0.15, 0.05, TRIM, [s * 0.44, 0.585, 2.08])); // black lamp surround
    P.push(box(0.62, 0.11, 0.06, LAMP, [s * 0.44, 0.585, 2.105])); // wide flat rectangular headlamp face
    P.push(box(0.52, 0.025, 0.02, "#9fb2c0", [s * 0.44, 0.55, 2.135])); // low-beam band
  }
  P.push(box(1.84, 0.24, 0.16, CLAD, [0, 0.38, 2.05])); // black front bumper / valance (front z 2.13)
  P.push(box(0.9, 0.08, 0.05, TRIM, [0, 0.32, 2.14])); // lower intake slot in the bumper
  P.push(box(0.44, 0.11, 0.02, PLATE, [0, 0.52, 2.14])); // front plate
  P.push(cyl(0.035, 0.035, 0.02, 14, CHROME, [0, 0.71, 2.11], [Math.PI / 2, 0, 0])); // small nose badge disc

  // -- Rear fascia: black slatted tail panel, wide ribbed lamps, twin exhausts -
  P.push(box(1.76, 0.28, 0.08, TRIM, [0, 0.605, -2.03])); // black slatted tail panel (y 0.465..0.745, back z -2.07)
  for (let i = -6; i <= 6; i++) P.push(box(0.02, 0.22, 0.02, "#26282c", [i * 0.12, 0.605, -2.075])); // fine tail-panel slats
  for (const s of S) {
    P.push(box(0.68, 0.16, 0.06, TAIL_RED, [s * 0.44, 0.6, -2.08])); // wide rectangular tail lamp
    for (let i = 0; i < 5; i++) P.push(box(0.02, 0.14, 0.06, REDDARK, [s * 0.44 + (i - 2) * 0.14, 0.6, -2.095])); // lamp ribs
    P.push(box(0.06, 0.05, 0.04, REDDARK, [s * 0.76, 0.47, -2.09])); // low reflector
  }
  P.push(box(1.84, 0.24, 0.16, CLAD, [0, 0.36, -2.05])); // black rear bumper (back z -2.13)
  P.push(box(0.44, 0.11, 0.02, PLATE, [0, 0.47, -2.11])); // rear plate
  for (const s of S) P.push(cyl(0.05, 0.05, 0.1, 14, EXHAUST, [s * 0.22, 0.28, -2.1], [Math.PI / 2, 0, 0])); // twin exhausts

  // -- Furniture: mirrors on the door tops, flush pulls -----------------------
  for (const s of S) {
    P.push(box(0.1, 0.03, 0.04, TRIM, [s * 0.92, 0.76, 0.42])); // mirror stalk overlapping the body
    P.push(paint(0.06, 0.09, 0.11, [s * 0.99, 0.78, 0.42])); // mirror head
    P.push(box(0.012, 0.07, 0.09, GLASS, [s * 1.025, 0.78, 0.43])); // mirror glass, facing back
    P.push(box(0.02, 0.03, 0.14, "#2c2f33", [s * 0.945, 0.64, -0.05])); // flush door pull
  }
  for (const ind of INDICATORS) {
    P.push(box(ind.size[0], ind.size[1], ind.size[2], AMBER, ind.pos, undefined, { name: ind.name }));
  }

  return P;
}

// One wheel revolution per cycle; positive x-rotation rolls the car toward +Z.
const WHEEL_PIVOTS: ReadonlyArray<{ name: string; pivot: Vec3 }> = [
  { name: "wheel-fl", pivot: [-WHEEL_X_F, WHEEL_R, AXLE_F] },
  { name: "wheel-fr", pivot: [WHEEL_X_F, WHEEL_R, AXLE_F] },
  { name: "wheel-rl", pivot: [-WHEEL_X_R, WHEEL_R, AXLE_R] },
  { name: "wheel-rr", pivot: [WHEEL_X_R, WHEEL_R, AXLE_R] },
];

export const deloreanDmc12 = defineModel({
  id: "delorean-dmc12",
  name: "DeLorean DMC-12",
  batchId: "cars",
  tags: ["car", "delorean", "dmc12", "coupe", "sports", "classic", "wedge"],
  summary: "A 1981 DeLorean DMC-12 — the brushed-stainless folded-paper wedge — at true scale.",
  description:
    "A 1981 DeLorean DMC-12 at real 1 unit = 1 metre scale (about 4.27 long, an " +
    "emphatically wide ~1.96, a very low ~0.96 tall, 2.41 wheelbase, ~0.32 m " +
    "wheels). The silhouette is a low, wide, angular Giugiaro wedge of flat " +
    "faceted planes and sharp edges — an unmistakable low wide letterbox from " +
    "the front and back: a low blunt nose rises through a near-flat hood and a " +
    "hard-raked flat windscreen to a low flat roof (a thin glass slot, not a " +
    "stacked cab), then a raked rear window buried under horizontal black " +
    "louvres falls to a short engine deck and a flat Kamm tail as one " +
    "continuous face (no flat deck behind the cabin). The signature cues are " +
    "all present: bare brushed stainless-steel bodywork (flat medium grey), a " +
    "prominent black rubber lower cladding band wrapping the whole car (nose " +
    "valance, sills, wheel arches, rear valance), gullwing door cut-lines arcing " +
    "onto the roof edge, two wide flat rectangular headlamps spanning most of " +
    "the wedge nose over a thin black grille slot, the black louvred engine " +
    "cover, a black slatted tail panel with wide ribbed tail lamps and twin " +
    "exhausts, and the DMC-12's bright multi-spoke turbine alloys on low-profile " +
    "tyres. Body panels carry the 'paint' recolour slot (brushed steel grey); " +
    "`drive` spins the wheels and `hazards` blinks the four indicators.",
  author: "Fable 5",
  footprint: 2.27,
  parts,
  animations: [
    {
      id: "drive",
      label: "Drive",
      duration: 1,
      tracks: WHEEL_PIVOTS.map(({ name, pivot }) => ({
        target: name,
        pivot,
        rotation: [
          { t: 0, value: [0, 0, 0] as Vec3 },
          { t: 0.5, value: [Math.PI, 0, 0] as Vec3 },
          { t: 1, value: [2 * Math.PI, 0, 0] as Vec3 },
        ],
      })),
    },
    {
      id: "hazards",
      label: "Hazard lights",
      duration: 1,
      tracks: INDICATORS.map(({ name, pos }) => ({
        target: name,
        pivot: pos,
        scale: [
          { t: 0, value: 1 },
          { t: 0.42, value: 1 },
          { t: 0.5, value: 0.02 },
          { t: 0.92, value: 0.02 },
          { t: 1, value: 1 },
        ],
      })),
    },
  ],
});
